<template>
  <div class="row-editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <i class="material-icons mr-2"> functions </i>
        <div>{{ inputs.name || moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display Label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field"
            message="">
            <PivotLabelInput
              v-model="inputs.name"
              :has-pivot="!!context.hasPivot"
              :pivot-label="context.pivotDimensionLabel || ''" />
          </b-field>

          <b-field class="mt-3">
            <b-switch
              v-model="hideLabel"
              class="mt-0"
              type="is-info"
              :disabled="!hasDisplayLabel">
              Hide label in PDF
            </b-switch>
          </b-field>
        </div>
      </div>

      <div
        id="measure"
        class="editor-section">
        <h2>Measure</h2>
        <div class="body">
          <MeasureConfigEditor
            v-model="inputs.measure_config"
            :context="context"
            :errors="childErrors('measure_config', errors)" />
        </div>
      </div>

      <div
        id="benchmark"
        class="editor-section">
        <h2>Benchmark</h2>
        <div class="body">
          <BenchmarkConfigEditor
            v-model="inputs.comparison_config"
            :errors="childErrors('comparison_config', errors)"
            :context="{
              ...context,
              parentMeasure: benchmarkParentMeasure,
              parentIsPctTotal: benchmarkParentIsPctTotal,
              timeRangeDefaultLabel: 'Use measure default',
              timeRangeDefaultName: benchmarkTimeRangeDefaultName,
            }" />
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :measure="inputs.measure_config?.measure"
            :type-override="formatTypeOverride" />
        </div>
      </div>

      <div
        id="header-style"
        class="editor-section">
        <h2>Header Style</h2>
        <div class="body">
          <b-field
            label="Column Width"
            class="input-field"
            message="">
            <b-numberinput
              v-model="inputs.column_width"
              min="0" />
          </b-field>
          <b-field
            class="input-field"
            label="Horizontal Alignment">
            <b-select
              v-model="inputs.header_style.h_align"
              placeholder="Select alignment"
              expanded>
              <option value="left">
                Left
              </option>
              <option value="center">
                Center
              </option>
              <option value="right">
                Right
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div
        id="cell-style"
        class="editor-section">
        <h2>Cell Style</h2>
        <div class="body">
          <b-field
            class="input-field"
            label="Horizontal Alignment">
            <b-select
              v-model="inputs.data_style.h_align"
              placeholder="Select alignment"
              expanded>
              <option value="left">
                Left
              </option>
              <option value="center">
                Center
              </option>
              <option value="right">
                Right
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import {
    throttle
  } from 'lodash'
  import MeasureConfigEditor from '@/components/reports/config/modules/common/MeasureConfigEditor'
  import BenchmarkConfigEditor from '@/components/reports/config/modules/common/BenchmarkConfigEditor'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import PivotLabelInput from '@/components/pdf_report/PivotLabelInput.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'MeasureEditor',
    components: {
      Tabs,
      MeasureConfigEditor,
      BenchmarkConfigEditor,
      DataFormatEditor,
      PivotLabelInput,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      context: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
    },

    data () {
      return {
        moduleConfigs: {
          name: 'Measure',
          hideInLibrary: true,
        },

        inputSectionTab: 'display-label',
      }
    },

    computed: {
      tabs () {
        return [
          {
            label: 'Display Label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Measure',
            id: 'measure',
            hasErrors: _childErrors('measure_config', this.errors).length > 0,
          },
          {
            label: 'Benchmark',
            id: 'benchmark',
            hasErrors: _childErrors('comparison_config', this.errors).length > 0,
          },
          {
            label: 'Format',
            id: 'format',
            hasErrors: _childErrors('data_format', this.errors).length > 0,
          },
          {
            label: 'Header Style',
            id: 'header-style',
            hasErrors: this.inputs?.header_style?.errors,
          },
          {
            label: 'Cell Style',
            id: 'cell-style',
            hasErrors: this.inputs?.data_style?.errors,
          },
        ]
      },
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      throttleScroll () {
        return throttle(this.onScroll, 100)
      },
      benchmarkParentMeasure () {
        return this.inputs.measure_config?.measure || null
      },
      benchmarkParentIsPctTotal () {
        return !!this.inputs.measure_config?.is_pct_total
      },
      benchmarkTimeRangeDefaultName () {
        return this.inputs.measure_config?.time_range?.name || this.context.tableDefaultTimeRange?.name || ''
      },
      formatTypeOverride () {
        if (this.inputs.measure_config?.is_pct_total) return 'percentage'
        if (this.inputs.comparison_config?.difference_type === 'var') return 'percentage'
        return null
      },
      hasDisplayLabel () {
        return !!(this.inputs?.name && this.inputs.name.trim().length > 0)
      },
      hideLabel: {
        get () {
          return !(this.inputs?.label_style?.show_label ?? true)
        },
        set (val) {
          if (!this.inputs.label_style) {
            this.$set(this.inputs, 'label_style', {
              type: 'LabelStyle',
              show_label: !val,
            })
          } else {
            this.$set(this.inputs.label_style, 'show_label', !val)
          }
        },
      },
    },

    methods: {
      childErrors: _childErrors,
      onScroll (event) {
        if (this._scrollingToSection) return
        const container = event.target
        const scrollTop = container.scrollTop
        const scrollingDown = scrollTop >= (this._lastScrollTop || 0)
        this._lastScrollTop = scrollTop
        const scrollableDivRect = container.getBoundingClientRect()
        const threshold = 10
        let activeSection = this.tabs[0].id
        for (const tab of this.tabs) {
          const el = container.querySelector(`#${tab.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= scrollableDivRect.top + threshold) {
            activeSection = tab.id
          }
        }
        const currentIndex = this.tabs.findIndex(t => t.id === this.inputSectionTab)
        const newIndex = this.tabs.findIndex(t => t.id === activeSection)
        if (scrollingDown && newIndex >= currentIndex) {
          this.inputSectionTab = activeSection
        } else if (!scrollingDown && newIndex <= currentIndex) {
          this.inputSectionTab = activeSection
        }
      },
      scrollToSection (sectionId) {
        this._scrollingToSection = true
        this.inputSectionTab = sectionId
        const container = this.$el.querySelector('.editor')
        const el = container && container.querySelector(`#${sectionId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', })
        }
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(() => { this._scrollingToSection = false }, 400)
      },
    },
  }
</script>

<style lang="scss" scoped>
.editor-header {
  border-bottom: $border-1;
  padding: 20px;
  background: white;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    margin-bottom: 15px;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.row-editor-container {
  width: 100%;
  height: 100%;
  margin: auto !important;
  background: #fbfbfb;
  display: flex;
  flex-direction: column;
  padding: 0px;
}

.editor {
  padding: 20px;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;

  .editor-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h2 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      text-transform: capitalize;
    }

    .body {
      border: $border-1;
      border-radius: 10px;
      padding: 18px;
      background: white;
    }
  }
}
</style>
